/* eslint-disable no-unused-vars */
import React, { useState } from "react";
import { motion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { auth } from "../firebase";
import { RecaptchaVerifier, signInWithPhoneNumber } from "firebase/auth";
import { getFirestore, collection, query, where, getDocs } from "firebase/firestore";

export default function AuthPage() {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [userType, setUserType] = useState("partner");
  const [countryCode, setCountryCode] = useState("+91");
  const [showDropdown, setShowDropdown] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const countries = [
    { code: "+91", name: "India", flag: "🇮🇳" },
    { code: "+1", name: "USA", flag: "🇺🇸" },
  ];

  const handlePhoneInput = (e) => {
    const value = e.target.value.replace(/\D/g, "");
    setPhoneNumber(value);
  };

  const handleOtpChange = (index, value) => {
    if (value.length > 1) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  const setupRecaptcha = () => {
  if (window.recaptchaVerifier) {
    window.recaptchaVerifier.clear();
  }

  window.recaptchaVerifier = new RecaptchaVerifier(
    auth,
    "recaptcha-container",
    {
      size: "invisible",
    }
  );

  return window.recaptchaVerifier.render();
};


  const handleSendOtp = async () => {
    if (phoneNumber.length !== 10) {
      alert("Please enter a valid 10-digit number");
      return;
    }

    setLoading(true);
    setupRecaptcha();
    const appVerifier = window.recaptchaVerifier;
    const fullPhone = `${countryCode}${phoneNumber}`;

    try {
      const confirmation = await signInWithPhoneNumber(auth, fullPhone, window.recaptchaVerifier);
      setConfirmationResult(confirmation);
      setOtpSent(true);
    } catch (error) {
      console.error("OTP Error:", error);
      alert("Failed to send OTP. Check Firebase setup.");
    } finally {
      setLoading(false);
    }
  };

  const db = getFirestore();

const handleVerifyOtp = async () => {
  const otpValue = otp.join("");
  if (!confirmationResult) return alert("No OTP sent!");

  setLoading(true);

  try {
    const result = await confirmationResult.confirm(otpValue);
    const user = result.user;

    // ---------------------------------------------------------
    // 🔍 STEP 1: CHECK FIRESTORE "vendors" COLLECTION
    // ---------------------------------------------------------
    const vendorsRef = collection(db, "vendors");
    const q = query(vendorsRef, where("mobileNumber", "==", phoneNumber));
    const snapshot = await getDocs(q);

    if (!snapshot.empty) {
      // Vendor exists in Firestore → dashboard
      alert("Welcome back Partner! Redirecting to dashboard...");
      window.location.href = "/dashboard";
      return;
    }

    // ---------------------------------------------------------
// STEP 2: VENDOR NOT FOUND → Call Backend API (Flutter Logic)
// ---------------------------------------------------------
try {
  const response = await fetch(
    "https://evpbackend-icy-morning-3358.fly.dev/api/create-user/",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        mobileNumber: phoneNumber,
        memberType: userType.toLowerCase(),
        fcmToken: "web-token-123" // (optional placeholder)
      }),
    }
  );

  const data = await response.json();
  console.log("Backend API Response:", data);

  if (!response.ok) {
    throw new Error(data.message || "Failed to authenticate user");
  }

  // Switch logic EXACTLY like Flutter makeUser()
  switch (data.status) {
    case "completed":
      // full profile exists
      alert("Login Successful!");

      // Save userId in localStorage
      localStorage.setItem("userId", data.id);

      if (userType === "partner") {
        window.location.href = "/dashboard-partner";
      } else {
        window.location.href = "/dashboard";
      }
      break;

    case "partial":
      // some fields missing
      alert(data.message || "Profile incomplete. Continue registration.");

      // Pass user/vendor data through navigation
      if (userType === "partner") {
        localStorage.setItem("vendorData", JSON.stringify(data.vendorData));
        window.location.href = "/partner-registration";
      } else {
        localStorage.setItem("userData", JSON.stringify(data.data));
        window.location.href = "/customer-registration";
      }
      break;

    case "false":
      // new user → number saved but no profile
      alert(data.message || "Please complete your profile.");

      if (userType === "partner") {
        localStorage.setItem("vendorData", JSON.stringify(data.vendorData));
        window.location.href = "/partner-registration";
      } else {
        localStorage.setItem("userData", JSON.stringify(data.data));
        window.location.href = "/customer-registration";
      }
      break;

    case "error":
    default:
      alert(data.message || "Authentication error. Try again later.");
      break;
  }
} catch (error) {
  alert("Authentication failed. Please try again.");
  console.error("Backend API Error:", error);
}

  } catch (error) {
    console.error("Verification Error:", error);
    alert("Invalid OTP. Try again.");
  } finally {
    setLoading(false);
  }
};

  const resetForm = () => {
    setOtpSent(false);
    setOtp(["", "", "", "", "", ""]);
    setPhoneNumber("");
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Left Side */}
      <div className="w-full lg:w-2/5 flex items-center justify-center p-6 sm:p-10 bg-gray-50">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8">
            {/* User Type Buttons */}
            <div className="flex gap-3 mb-6">
              {["partner", "customer"].map((type) => (
                <motion.button
                  key={type}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setUserType(type)}
                  className={`flex-1 py-2.5 px-4 rounded-lg font-medium transition-all ${userType === type
                    ? "bg-orange-500 text-white shadow-md"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                >
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </motion.button>
              ))}
            </div>

            <h1 className="text-2xl sm:text-3xl font-bold text-indigo-900 mb-2">
              {otpSent ? "Verify OTP" : "Welcome"}
            </h1>

            <p className="text-gray-500 text-sm mb-6">
              {otpSent
                ? `Enter the OTP sent to ${countryCode} ${phoneNumber}`
                : "Enter your phone number to continue"}
            </p>

            <div id="recaptcha-container"></div>

            <div>
              {!otpSent ? (
                <>
                  {/* Phone Input */}
                  <div className="mb-6">
                    <label className="block text-indigo-900 text-sm font-medium mb-2">
                      Phone Number*
                    </label>

                    <div className="flex gap-2">
                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => setShowDropdown(!showDropdown)}
                          className="flex items-center gap-2 px-3 py-3 rounded-lg border border-gray-300 bg-white hover:bg-gray-50"
                        >
                          <span className="text-lg">
                            {countries.find((c) => c.code === countryCode)?.flag}
                          </span>
                          <span className="text-sm font-medium">{countryCode}</span>
                          <ChevronDown size={16} className="text-gray-500" />
                        </button>

                        {showDropdown && (
                          <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="absolute top-full mt-1 left-0 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-10"
                          >
                            {countries.map((country) => (
                              <button
                                key={country.code}
                                type="button"
                                onClick={() => {
                                  setCountryCode(country.code);
                                  setShowDropdown(false);
                                }}
                                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50"
                              >
                                <span className="text-lg">{country.flag}</span>
                                <span className="text-sm font-medium">{country.name}</span>
                                <span className="ml-auto text-sm text-gray-500">
                                  {country.code}
                                </span>
                              </button>
                            ))}
                          </motion.div>
                        )}
                      </div>

                      <input
                        type="tel"
                        value={phoneNumber}
                        onChange={handlePhoneInput}
                        placeholder="9878XXXXXX"
                        maxLength={10}
                        className="flex-1 px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-900"
                      />
                    </div>
                  </div>

                  <motion.button
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.99 }}
                    onClick={handleSendOtp}
                    disabled={phoneNumber.length !== 10 || loading}
                    className="w-full bg-orange-500 text-white py-3 rounded-lg font-medium hover:bg-orange-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
                  >
                    {loading ? "Sending..." : "Continue"}
                  </motion.button>
                </>
              ) : (
                <>
                  {/* OTP */}
                  <div className="mb-6">
                    <label className="block text-indigo-900 text-sm font-medium mb-3">
                      Enter OTP*
                    </label>

                    <div className="grid grid-cols-6 gap-2 sm:gap-3">
                      {otp.map((digit, index) => (
                        <input
                          key={index}
                          id={`otp-${index}`}
                          type="text"
                          maxLength={1}
                          inputMode="numeric"
                          value={digit}
                          onChange={(e) =>
                            handleOtpChange(index, e.target.value.replace(/\D/g, ""))
                          }
                          onKeyDown={(e) => handleOtpKeyDown(index, e)}
                          className="w-full h-12 sm:h-14 text-center text-lg sm:text-xl font-semibold rounded-lg border-2 border-gray-300 focus:ring-2 focus:ring-indigo-900"
                        />
                      ))}
                    </div>
                  </div>

                  <div className="text-center mb-6">
                    <span className="text-sm text-gray-600">Didn't receive OTP? </span>
                    <button
                      onClick={() => handleSendOtp()}
                      disabled={loading}
                      className="text-sm text-orange-600 font-medium hover:underline disabled:opacity-50"
                    >
                      Resend
                    </button>
                  </div>

                  <motion.button
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.99 }}
                    onClick={handleVerifyOtp}
                    disabled={otp.some((d) => !d) || loading}
                    className="w-full bg-orange-500 text-white py-3 rounded-lg font-medium hover:bg-orange-600 disabled:bg-gray-300 disabled:cursor-not-allowed mb-4"
                  >
                    {loading ? "Verifying..." : "Verify & Continue"}
                  </motion.button>

                  <button
                    onClick={resetForm}
                    disabled={loading}
                    className="w-full text-sm text-gray-600 hover:text-indigo-900 font-medium disabled:opacity-50"
                  >
                    Change Phone Number
                  </button>
                </>
              )}
            </div>
          </div>
        </motion.div>
      </div>

      {/* Right Section Image */}
      <motion.div
        initial={{ opacity: 0, x: 50 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.7 }}
        className="hidden lg:block lg:w-3/5"
      >
        <img
          src="/images/event-image.jpg"
          alt="Event"
          className="w-full h-full object-cover"
        />
      </motion.div>
    </div>
  );
}

